import clsx from 'clsx';
import React, { createContext, useContext, useCallback, useRef, useState, useMemo, } from 'react';
import ChevronRight from '@mollie/ui-assets/icons/utilitarian/ChevronRight';
import ChevronLeft from '@mollie/ui-assets/icons/utilitarian/ChevronLeft';
import { useElementSize, useIsomorphicLayoutEffect } from '../../utils';
import { Button } from '../Button';
import { createDescendantsStepper, useDescendantsContextSelector, useDescendantsStepper, } from '../Descandants';
import { Icon } from '../Icon';
import { TabsDescendantsContext } from './Tabs';
import { TabsTab } from './TabsTab';
import { ButtonGroupContext, } from '../ButtonGroup/ButtonGroupContext';
var KeyboardActions;
(function (KeyboardActions) {
    KeyboardActions[KeyboardActions["SELECT"] = 0] = "SELECT";
    KeyboardActions[KeyboardActions["FIRST"] = 1] = "FIRST";
    KeyboardActions[KeyboardActions["LAST"] = 2] = "LAST";
    KeyboardActions[KeyboardActions["NEXT"] = 3] = "NEXT";
    KeyboardActions[KeyboardActions["PREVIOUS"] = 4] = "PREVIOUS";
    KeyboardActions[KeyboardActions["RESTORE"] = 5] = "RESTORE";
})(KeyboardActions || (KeyboardActions = {}));
const TabsTablistContext = createContext({
    onSelect: () => void 0,
});
const buttonGroupContextValue = { size: 'small' };
export const useTabsTablistContext = () => useContext(TabsTablistContext);
export function TabsTablist(props) {
    const { tabs, advanced, filled, rotate, ariaPreviousLabel, ariaNextLabel, postfix, } = props;
    const tabsDescendantsStepper = useMemo(() => createDescendantsStepper({
        rotate,
    }), [rotate]);
    const [scrollWidth, setScrollWidth] = useState(0);
    const [elementWidth, setElementWidth] = useState(0);
    const [overflowRightDisabled, setOverflowRightDisabled] = useState(false);
    const [overflowLeftDisabled, setOverflowLeftDisabled] = useState(false);
    const tablistContentRef = useRef(null);
    const setSelectedIndex = useDescendantsContextSelector(TabsDescendantsContext, ({ setSelectedIndex }) => setSelectedIndex);
    const setActiveIndex = useDescendantsContextSelector(TabsDescendantsContext, ({ setActiveIndex }) => setActiveIndex);
    const selected = useDescendantsContextSelector(TabsDescendantsContext, ({ selected }) => selected);
    const lastUsedKey = useRef();
    useElementSize(tablistContentRef, ({ width }, element) => {
        const { paddingLeft, paddingRight } = getComputedStyle(element);
        setElementWidth(width);
        setScrollWidth(element.scrollWidth - parseFloat(paddingLeft) - parseFloat(paddingRight));
        setOverflowLeftDisabled(isAtBounds('left'));
        setOverflowRightDisabled(isAtBounds('right'));
    });
    const stepper = useDescendantsStepper(TabsDescendantsContext, tabsDescendantsStepper);
    const onSelect = useCallback((index) => {
        setSelectedIndex(index);
        setActiveIndex(index);
    }, [setActiveIndex, setSelectedIndex]);
    const onKeyDown = (e) => {
        var _a, _b, _c, _d;
        const { key } = e;
        lastUsedKey.current = key;
        const actionKey = (() => {
            if (key === 'Home')
                return KeyboardActions.FIRST;
            if (key === 'End')
                return KeyboardActions.LAST;
            if (key === 'ArrowRight')
                return KeyboardActions.NEXT;
            if (key === 'ArrowLeft')
                return KeyboardActions.PREVIOUS;
            if (key === 'Enter' || key === ' ')
                return KeyboardActions.SELECT;
            if (key === 'Tab')
                return KeyboardActions.RESTORE;
            return;
        })();
        switch (actionKey) {
            case KeyboardActions.SELECT:
                e.preventDefault();
                const activeDescendant = stepper.getActiveDescendant();
                if (activeDescendant) {
                    onSelect(activeDescendant.index);
                }
                break;
            case KeyboardActions.FIRST: {
                e.preventDefault();
                const nextElement = stepper.getFirstDescendant();
                if (nextElement) {
                    setActiveIndex(nextElement.index);
                    (_a = nextElement.element) === null || _a === void 0 ? void 0 : _a.focus();
                }
                break;
            }
            case KeyboardActions.LAST: {
                e.preventDefault();
                const nextElement = stepper.getLastDescendant();
                if (nextElement) {
                    setActiveIndex(nextElement.index);
                    (_b = nextElement.element) === null || _b === void 0 ? void 0 : _b.focus();
                }
                break;
            }
            case KeyboardActions.NEXT: {
                e.preventDefault();
                const nextElement = stepper.getNextDescendant();
                if (nextElement) {
                    setActiveIndex(nextElement.index);
                    (_c = nextElement.element) === null || _c === void 0 ? void 0 : _c.focus();
                }
                break;
            }
            case KeyboardActions.PREVIOUS: {
                e.preventDefault();
                const nextElement = stepper.getPreviousDescendant();
                if (nextElement) {
                    setActiveIndex(nextElement.index);
                    (_d = nextElement.element) === null || _d === void 0 ? void 0 : _d.focus();
                }
                break;
            }
        }
    };
    const hasOverflow = advanced && scrollWidth > elementWidth;
    function isAtBounds(direction, scrollLeft) {
        if (!tablistContentRef.current)
            return false;
        const { width } = tablistContentRef.current.getBoundingClientRect();
        scrollLeft = scrollLeft !== null && scrollLeft !== void 0 ? scrollLeft : tablistContentRef.current.scrollLeft;
        return direction === 'left'
            ? scrollLeft <= 0
            : Math.ceil(scrollLeft + width) >= tablistContentRef.current.scrollWidth;
    }
    function scroll(direction) {
        if (!tablistContentRef.current || isAtBounds(direction))
            return;
        const { width } = tablistContentRef.current.getBoundingClientRect();
        tablistContentRef.current.scrollBy({
            left: direction === 'left' ? -width : width,
            behavior: 'smooth',
        });
        const nextScrollLeft = direction === 'left'
            ? tablistContentRef.current.scrollLeft - width
            : tablistContentRef.current.scrollLeft + width;
        setOverflowLeftDisabled(isAtBounds('left', nextScrollLeft));
        setOverflowRightDisabled(isAtBounds('right', nextScrollLeft));
    }
    useIsomorphicLayoutEffect(() => {
        if (selected.length > 0) {
            return;
        }
        const defaultSelectedIndex = tabs.findIndex((tab) => tab.props.defaultSelected === true);
        if (defaultSelectedIndex > -1) {
            onSelect(defaultSelectedIndex);
        }
    }, [onSelect, selected.length, tabs]);
    return (React.createElement(TabsTablistContext.Provider, { value: useMemo(() => ({ onSelect }), [onSelect]) },
        React.createElement("div", { className: clsx('mollie-ui-tabs-tablist', {
                'mollie-ui-tabs-tablist--has-overflow': hasOverflow,
            }) },
            hasOverflow && (React.createElement("div", { className: "mollie-ui-tabs-tablist-overflow" },
                React.createElement(Button, { unstyled: true, tabIndex: -1, disabled: overflowLeftDisabled, className: "mollie-ui-tabs-tablist-overflow-button", onClick: () => scroll('left'), "aria-label": ariaPreviousLabel },
                    React.createElement(Icon, { src: ChevronLeft })))),
            React.createElement("div", { className: clsx('mollie-ui-tabs-tablist-content', {
                    'mollie-ui-tabs-tablist-content--filled': filled,
                }), ref: tablistContentRef, role: "tablist", onKeyDown: onKeyDown, onMouseDown: () => {
                    lastUsedKey.current = null;
                }, onScroll: (e) => {
                    setOverflowLeftDisabled(isAtBounds('left', e.currentTarget.scrollLeft));
                    setOverflowRightDisabled(isAtBounds('right', e.currentTarget.scrollLeft));
                }, onFocus: (e) => {
                    var _a, _b;
                    if (lastUsedKey.current !== null &&
                        !((_a = tablistContentRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)) &&
                        selected[0]) {
                        setActiveIndex(selected[0].index);
                        (_b = selected[0].element) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                } },
                React.createElement("div", { className: clsx('mollie-ui-tabs-tablist-content-wrapper', {
                        'mollie-ui-tabs-tablist-content-wrapper--filled': filled,
                    }) },
                    tabs.map((panel, index) => (React.createElement(TabsTab, { ...panel.props, key: panel.key, index: index }))),
                    postfix && (React.createElement(ButtonGroupContext.Provider, { value: buttonGroupContextValue },
                        React.createElement("div", { className: "mollie-ui-tabs-tablist-postfix" }, postfix))))),
            hasOverflow && (React.createElement("div", { className: "mollie-ui-tabs-tablist-overflow" },
                React.createElement(Button, { unstyled: true, tabIndex: -1, disabled: overflowRightDisabled, className: "mollie-ui-tabs-tablist-overflow-button", onClick: () => scroll('right'), "aria-label": ariaNextLabel },
                    React.createElement(Icon, { src: ChevronRight })))))));
}
TabsTablist.displayName = 'Tabs.Tablist';
//# sourceMappingURL=TabsTabList.js.map